import { useEffect, useState, useCallback } from "react";
import {
  Wallet, TrendingUp, TrendingDown, Percent, ArrowUpRight, ArrowDownRight, Building2, Plus,
  Car, FileText, AlertTriangle, ChevronRight, CalendarClock,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { eur, eurShort, fmtDate, CHART_COLORS } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import TransactionModal from "@/components/TransactionModal";
import { FattureDaAssegnare } from "@/components/FattureDaAssegnare";

const YEARS = [2024, 2025, 2026, 2027];

function StatCard({ testid, label, value, sub, icon: Icon, tone }) {
  const tones = {
    blue: "text-sky-600 bg-sky-50 dark:bg-sky-950/40 dark:text-sky-300",
    emerald: "text-emerald-600 bg-emerald-50 dark:bg-emerald-950/40 dark:text-emerald-300",
    amber: "text-amber-600 bg-amber-50 dark:bg-amber-950/40 dark:text-amber-300",
    indigo: "text-indigo-600 bg-indigo-50 dark:bg-indigo-950/40 dark:text-indigo-300",
  };
  return (
    <Card data-testid={testid} className="hover:border-slate-300 dark:hover:border-slate-700 transition-colors animate-fade-up">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
            <p className="mt-2 text-2xl font-bold tracking-tight font-mono tabular-nums">{value}</p>
            {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
          </div>
          <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${tones[tone]}`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [year, setYear] = useState(new Date().getFullYear());
  const [scope, setScope] = useState("anno"); // anno | mese
  const [summary, setSummary] = useState(null);
  const [cashflow, setCashflow] = useState([]);
  const [breakdown, setBreakdown] = useState([]);
  const [recent, setRecent] = useState([]);
  const [categories, setCategories] = useState(null);
  const [cantieri, setCantieri] = useState([]);
  const [fornitori, setFornitori] = useState([]);
  const [alerts, setAlerts] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const load = useCallback(async () => {
    const month = scope === "mese" ? new Date().getMonth() + 1 : undefined;
    const [s, cf, bd, tx, cats, cant, forn, al] = await Promise.all([
      api.get("/dashboard/summary", { year, month }),
      api.get("/analytics/cashflow", { year }),
      api.get("/analytics/cost-breakdown", { year }),
      api.get("/transactions", { limit: 6 }),
      api.get("/categories"),
      api.get("/cantieri"),
      api.get("/fornitori"),
      api.get("/dashboard/alerts"),
    ]);
    setSummary(s); setCashflow(cf); setBreakdown(bd);
    setRecent(tx.items); setCategories(cats); setCantieri(cant); setFornitori(forn); setAlerts(al);
  }, [year, scope]);

  useEffect(() => { load(); }, [load]);

  const scopeLabel = scope === "mese" ? "questo mese" : "quest'anno";

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-1">Panoramica economica dell'impresa.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={scope} onValueChange={setScope}>
            <SelectTrigger className="w-[140px]" data-testid="dashboard-scope-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="anno">Anno intero</SelectItem>
              <SelectItem value="mese">Mese corrente</SelectItem>
            </SelectContent>
          </Select>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="w-[100px]" data-testid="dashboard-year-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={() => setModalOpen(true)} data-testid="add-transaction-button"
            className="gap-2 bg-slate-900 hover:bg-slate-800 dark:bg-amber-500 dark:hover:bg-amber-600 dark:text-slate-900">
            <Plus className="h-4 w-4" /> <span className="hidden sm:inline">Transazione</span>
          </Button>
        </div>
      </div>

      {alerts && (
        <div className="space-y-3">
          {alerts.fatture_scadute > 0 && (
            <button onClick={() => navigate("/transazioni", { state: { scadute: true } })} data-testid="alert-scadute-card"
              className="w-full flex items-center justify-between gap-4 rounded-xl border-2 border-red-300 bg-red-50 dark:border-red-500/40 dark:bg-red-950/20 px-5 py-4 text-left transition-all hover:shadow-md animate-fade-up">
              <div className="flex items-center gap-3 min-w-0">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-500/15 text-red-600 shrink-0"><CalendarClock className="h-5 w-5" /></div>
                <div className="min-w-0">
                  <p className="font-semibold text-red-700 dark:text-red-300">Fatture scadute non pagate</p>
                  <p className="text-xs text-muted-foreground truncate">Totale da saldare {eur(alerts.fatture_scadute_importo)}</p>
                </div>
              </div>
              <div className="flex items-center gap-4 shrink-0">
                <div className="text-right"><p className="text-2xl font-bold text-red-600 font-mono">{alerts.fatture_scadute}</p><p className="text-[10px] uppercase text-muted-foreground">scadute</p></div>
                <span className="rounded-lg bg-red-600 text-white px-3 py-1.5 text-sm font-medium items-center gap-1 hidden sm:flex">Visualizza <ChevronRight className="h-4 w-4" /></span>
              </div>
            </button>
          )}

          <button onClick={() => navigate("/archivio?tab=mezzi")} data-testid="alert-veicoli-card"
            className={`w-full flex items-center justify-between gap-4 rounded-xl border-2 px-5 py-4 text-left transition-all hover:shadow-md animate-fade-up ${
              alerts.veicoli_scaduti > 0
                ? "border-red-300 bg-red-50 dark:border-red-500/40 dark:bg-red-950/20"
                : alerts.veicoli_in_scadenza > 0
                ? "border-amber-300/70 bg-amber-50 dark:bg-amber-950/20"
                : "border-border bg-card"}`}>
            <div className="flex items-center gap-3 min-w-0">
              <div className={`flex h-10 w-10 items-center justify-center rounded-lg shrink-0 ${
                alerts.veicoli_scaduti > 0 ? "bg-red-500/15 text-red-600" : alerts.veicoli_in_scadenza > 0 ? "bg-amber-500/20 text-amber-600" : "bg-secondary text-muted-foreground"}`}><Car className="h-5 w-5" /></div>
              <div className="min-w-0">
                <p className={`font-semibold ${alerts.veicoli_scaduti > 0 ? "text-red-700 dark:text-red-300" : alerts.veicoli_in_scadenza > 0 ? "text-amber-700 dark:text-amber-300" : ""}`}>Scadenze Veicoli</p>
                <p className="text-xs text-muted-foreground truncate">
                  {alerts.veicoli_scaduti > 0
                    ? `Revisione/assicurazione: ${alerts.veicoli_scaduti} scaduti · azione richiesta`
                    : alerts.veicoli_in_scadenza > 0
                    ? `${alerts.veicoli_in_scadenza} in scadenza entro 15 giorni`
                    : "Revisione e assicurazione: tutto in regola"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4 shrink-0">
              <div className="text-right">
                <p className={`text-2xl font-bold font-mono ${alerts.veicoli_scaduti > 0 ? "text-red-600" : alerts.veicoli_in_scadenza > 0 ? "text-amber-600" : "text-muted-foreground"}`}>
                  {(alerts.veicoli_scaduti + alerts.veicoli_in_scadenza) > 0 ? (alerts.veicoli_scaduti + alerts.veicoli_in_scadenza) : alerts.veicoli_totali}
                </p>
                <p className="text-[10px] uppercase text-muted-foreground">{(alerts.veicoli_scaduti + alerts.veicoli_in_scadenza) > 0 ? "da controllare" : "veicoli totali"}</p>
              </div>
              <span className={`rounded-lg text-white px-3 py-1.5 text-sm font-medium items-center gap-1 hidden sm:flex ${alerts.veicoli_scaduti > 0 ? "bg-red-600" : "bg-amber-500"}`}>Visualizza <ChevronRight className="h-4 w-4" /></span>
            </div>
          </button>

          <FattureDaAssegnare
            tipo="uscita" title="Fatture da rendicontare"
            subtitle="Fatture ricevute · classifica le uscite (commessa facoltativa)"
            count={alerts.fatture_da_assegnare} cantieri={cantieri} categories={categories}
            onChanged={load} showImport
          />

          {isOwner && (
          <FattureDaAssegnare
            tipo="entrata" title="Entrate da rendicontare"
            subtitle="Fatture emesse · classifica gli incassi (commessa facoltativa)"
            count={alerts.entrate_da_assegnare || 0} cantieri={cantieri} categories={categories}
            onChanged={load}
          />
          )}

          <button onClick={() => navigate("/scadenze")} data-testid="alert-formazione-card"
            className={`w-full flex items-center justify-between gap-4 rounded-xl border-2 px-5 py-4 text-left transition-all hover:shadow-md animate-fade-up ${alerts.formazione_scadute > 0 ? "border-red-300 bg-red-50 dark:border-red-500/40 dark:bg-red-950/20" : "border-border bg-card"}`}>
            <div className="flex items-center gap-3 min-w-0">
              <div className={`flex h-10 w-10 items-center justify-center rounded-lg shrink-0 ${alerts.formazione_scadute > 0 ? "bg-red-500/15 text-red-600" : "bg-secondary text-muted-foreground"}`}><AlertTriangle className="h-5 w-5" /></div>
              <div className="min-w-0">
                <p className={`font-semibold ${alerts.formazione_scadute > 0 ? "text-red-700 dark:text-red-300" : ""}`}>Scadenze Formazione Critiche</p>
                <p className="text-xs text-muted-foreground truncate">{alerts.formazione_scadute > 0 ? "Certificazioni scadute - Azione immediata richiesta" : alerts.formazione_in_scadenza > 0 ? `${alerts.formazione_in_scadenza} certificazioni in scadenza` : "Nessuna certificazione scaduta"}</p>
              </div>
            </div>
            <div className="flex items-center gap-4 shrink-0">
              <div className="text-right"><p className={`text-2xl font-bold font-mono ${alerts.formazione_scadute > 0 ? "text-red-600" : "text-muted-foreground"}`}>{alerts.formazione_scadute}</p><p className="text-[10px] uppercase text-muted-foreground">scadenze critiche</p></div>
              <span className={`rounded-lg px-3 py-1.5 text-sm font-medium items-center gap-1 hidden sm:flex text-white ${alerts.formazione_scadute > 0 ? "bg-red-600" : "bg-slate-900 dark:bg-slate-700"}`}>Visualizza <ChevronRight className="h-4 w-4" /></span>
            </div>
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {isOwner && <StatCard testid="da-incassare-card" label="Da Incassare" tone="emerald" icon={TrendingUp}
          value={eur(summary && alerts ? alerts.da_incassare : 0)} sub="Fatture emesse non ancora incassate" />}
        <StatCard testid="da-pagare-card" label="Da Pagare" tone="amber" icon={TrendingDown}
          value={eur(alerts ? alerts.da_pagare : 0)} sub="Fatture ricevute non ancora pagate" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {isOwner && <StatCard testid="total-balance-card" label="Saldo Totale" tone="blue" icon={Wallet}
          value={eur(summary?.saldo_totale)} sub={`Utile ${scopeLabel}`} />}
        {isOwner && <StatCard testid="total-revenue-card" label="Fatturato Totale" tone="emerald" icon={TrendingUp}
          value={eur(summary?.fatturato_totale)} sub={`${summary?.n_entrate || 0} entrate`} />}
        <StatCard testid="total-expenses-card" label="Costi Totali" tone="amber" icon={TrendingDown}
          value={eur(summary?.costi_totali)} sub={`${summary?.n_uscite || 0} uscite`} />
        {isOwner && <StatCard testid="net-profit-card" label="Utile Netto / Margine" tone="indigo" icon={Percent}
          value={eur(summary?.utile_netto)} sub={`Margine ${summary?.margine_pct ?? 0}%`} />}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <Card className="lg:col-span-3">
          <CardHeader className="pb-2"><CardTitle className="text-base">Flusso di Cassa {year}</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={cashflow} margin={{ top: 10, right: 0, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="mese" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={eurShort} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                <Tooltip
                  formatter={(v, n) => [eur(v), n]}
                  contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 12, fontSize: 13 }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {isOwner && <Bar dataKey="entrate" name="Entrate" fill="#10B981" radius={[4, 4, 0, 0]} />}
                <Bar dataKey="uscite" name="Uscite" fill="#D97706" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader className="pb-2"><CardTitle className="text-base">Ripartizione Costi</CardTitle></CardHeader>
          <CardContent>
            {breakdown.length === 0 ? (
              <div className="h-[320px] flex items-center justify-center text-sm text-muted-foreground">Nessun costo registrato.</div>
            ) : (
              <ResponsiveContainer width="100%" height={320}>
                <PieChart>
                  <Pie data={breakdown} dataKey="value" nameKey="name" cx="50%" cy="45%" innerRadius={55} outerRadius={90} paddingAngle={2}>
                    {breakdown.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => eur(v)}
                    contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 12, fontSize: 13 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-2 flex-row items-center justify-between">
          <CardTitle className="text-base">Movimenti Recenti</CardTitle>
        </CardHeader>
        <CardContent>
          {recent.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">Nessuna transazione. Aggiungine una per iniziare.</div>
          ) : (
            <div className="divide-y divide-border">
              {recent.map((t) => (
                <div key={t.id} className="flex items-center justify-between py-3" data-testid={`recent-tx-${t.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${
                      t.tipo === "entrata" ? "bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40" : "bg-red-50 text-red-600 dark:bg-red-950/40"}`}>
                      {t.tipo === "entrata" ? <ArrowUpRight className="h-4 w-4" /> : <ArrowDownRight className="h-4 w-4" />}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{t.descrizione}</p>
                      <p className="text-xs text-muted-foreground truncate flex items-center gap-1">
                        {t.categoria_label}{t.cantiere_nome && <><Building2 className="h-3 w-3" />{t.cantiere_nome}</>}
                      </p>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className={`text-sm font-semibold font-mono tabular-nums ${t.tipo === "entrata" ? "text-emerald-600" : "text-red-600"}`}>
                      {t.tipo === "entrata" ? "+" : "-"}{eur(t.importo)}
                    </p>
                    <p className="text-xs text-muted-foreground">{fmtDate(t.data)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <TransactionModal open={modalOpen} onOpenChange={setModalOpen} categories={categories} cantieri={cantieri} fornitori={fornitori} onSaved={load} />
    </div>
  );
}
